// @flow
import React, { useEffect, useState } from 'react';
import Chart from 'react-google-charts';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import API from 'services/API';
import type { ChartProps } from 'types/dashboard';
import { GranularityFormats } from 'UI/constants/defaults';
import { colors } from 'UI/res';
import { nestTernary } from 'UI/utils';

const lineChartOptions = (horizontalTicks, dateFormat, lineColors?) => ({
  legend: { position: 'top', alignment: 'end' },
  pointSize: 10,
  vAxis: {
    baselineColor: colors.lightgrey,
    gridlines: {
      color: colors.lightgrey
    },
    textStyle: {
      color: colors.darkGrey
    }
  },
  hAxis: {
    ticks: horizontalTicks,
    baselineColor: colors.lightgrey,
    gridlines: {
      color: colors.lightgrey
    },
    textStyle: {
      color: colors.darkGrey
    },
    format: dateFormat,
    viewWindowMode: 'pretty'
  },
  chartArea: {
    width: '90%',
    height: '75%'
  },
  colors: lineColors || undefined
});

const LineChart = ({ data, fetching, url, lastUpdate }: ChartProps) => {
  const height = 400;
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  const [horizontalTicks, setHorizontalTicks] = useState([]);

  const [granularity, setGranularity] = useState('month');
  const [lineColors, setLineColors] = useState();

  useEffect(() => {
    typeof fetching === 'boolean' && setIsLoading(fetching);
  }, [fetching]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const response = !data && url && (await API.get(url));
        if ((response.status === 200 && response.data?.data) || data?.data) {
          const auxData = response ? response.data : data;
          const preparedData = auxData.data.map(dataArray => {
            const [date, ...rest] = dataArray;
            return [new Date(date), ...rest];
          });
          const ticks = preparedData.map(series => series[0]);
          setHorizontalTicks(ticks);
          const [granId, grandDesc] = auxData.granularity;
          setGranularity(grandDesc === 'hour' && granId === 1 ? 'hour24' : grandDesc);
          setChartData([auxData.series, ...preparedData]);
          setLineColors(auxData.colors);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      }
      setIsLoading(false);
    })();
  }, [data, url, lastUpdate]);

  return isLoading ? (
    <Box display="flex" alignItems="center" justifyContent="center" style={{ minHeight: height }}>
      <CircularProgress />
    </Box>
  ) : (
    nestTernary(
      chartData.length > 1,
      <Chart
        width="100%"
        height={height}
        chartType="LineChart"
        loader={<div>Loading Chart</div>}
        data={chartData}
        options={lineChartOptions(horizontalTicks, GranularityFormats[granularity], lineColors)}
      />,
      <>There is no data with the selected filters</>
    )
  );
};

LineChart.defaultProps = {
  data: undefined,
  fetching: undefined,
  lastUpdate: '',
  url: ''
};

export default LineChart;
